//deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import {
    BillDetailItemDisplayProps,
    BillDetailItemTitleProps,
    BillDetailItemTooltipDisplayProps,
    ICollapsableBillDetailsWrapperProps,
    ISavingsStamp,
    isBillNotCollapsible,
} from "@swiggy-private/cart-ui";
import {
    BOOLEAN_AS_STRING,
    CollapsableBillDetailsVariants,
    PlanDetails,
    RenderingDetails,
} from "@swiggy-private/cart-types";
import { AnalyticsContext, DELIVERY_SECTION_ELEMENT, useSessionStorage } from "@swiggy-private/checkout-core";
import { scrollElementIntoView } from "@swiggy-private/core-ui";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { DeliverySectionContext } from "@cart-client/contexts/DeliverySectionContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

// helpers
import { getDiscountType, getFreeDelCommsStyling, getTotalSavings } from "@cart-client/helpers/Cart";
import { isOfferPackEnabled } from "@cart-client/helpers/OfferBundle";
import { getDefaultTipDetails } from "@cart-client/helpers/DeTip";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

// constant
import { VALID_RENDERING_BUTTON_TYPES } from "@cart-client/constants/index";
import { CART_ELEMENT_IDS } from "@cart-client/components/cart-renderer/constants";

// hooks
import { useCartRendererAnalytics } from "./useCartRenderedAnalytics";
import { useBillDetailsAnalytics } from "./useBillDetailsAnalytics";

// analytics
import { clickBillDetailAddTip } from "@cart-client/analytics/events";
import { renderHalfcardOnMakeItFreeClick } from "@cart-client/helpers/BillDetailsHelper";

export interface UseBillDetailRendererReturnType {
    displayData: Omit<BillDetailItemDisplayProps, "itemData" | "planAddRef">;
    tooltipDisplayData: Omit<BillDetailItemTooltipDisplayProps, "itemData" | "planAddRef">;
    titleData: Omit<BillDetailItemTitleProps, "itemData">;
    collapsibleWidgetData: Omit<ICollapsableBillDetailsWrapperProps, "children">;
    savingsStampData: ISavingsStamp;
}

export const useBillDetailsRenderer = (
    setBillDetailsOneCardComms?: VoidFunction,
    planDetails?: PlanDetails,
): UseBillDetailRendererReturnType => {
    const { cartState } = useContext(CartContext);
    const { data } = useContext(CartFlowContext);
    const { reviewedCart: cartResponse, localCart } = cartState;
    const { updateCartInProgress, doUpdateCart, setShowM2ChalfcardOnClick } = useContext(UpdateCartContext);
    const { clickEvent } = useContext(AnalyticsContext);
    const { setDeliveryTab } = useContext(DeliverySectionContext);

    const collapsedBillDetails = cartResponse?.data?.collapsed_bill_details;

    // for gifting cart, we want to show the bill details under new variant by default
    const collapseBillDetailXPVariant = useMemo(() => {
        if (isGiftingCartSource(data?.cartSource)) {
            return CollapsableBillDetailsVariants.VARIANT_WITHOUT_SAVING_UPSELL;
        }
        return cartResponse?.data?.collapsed_bill_details_variants || CollapsableBillDetailsVariants.DEFAULT;
    }, [data?.cartSource, cartResponse?.data?.collapsed_bill_details_variants]);

    // for gifting cart, we want to show the bill details uncollapsed by default
    const initialUncollapsedState = useMemo(() => {
        if (isGiftingCartSource(data?.cartSource)) {
            return true;
        }
        return collapsedBillDetails?.uncollapsed || false;
    }, [data?.cartSource, collapsedBillDetails?.uncollapsed]);

    const billDetailItemsData = useMemo(
        () => ({
            totalSavings: getTotalSavings(cartResponse?.data),
            discountType: getDiscountType(cartResponse?.data),
            isOfferPack: isOfferPackEnabled(cartResponse),
            freeDelCommsStyles: getFreeDelCommsStyling(cartResponse?.data),
        }),
        [cartResponse],
    );

    const updateTipDetails = useCallback(
        async ({ amount, optIn }: { amount: number; optIn: boolean }): Promise<void> => {
            if (!updateCartInProgress) {
                await doUpdateCart({
                    ...localCart,
                    tipDetails: {
                        amount,
                        optIn,
                    },
                });
            }
        },
        [doUpdateCart, updateCartInProgress, localCart],
    );

    const onItemClick = useCallback(
        (itemType?: string, itemData?: RenderingDetails) => {
            switch (itemType?.toUpperCase()) {
                case VALID_RENDERING_BUTTON_TYPES.ADD_DELIVERY_TIP: {
                    const defaultTipDetails = getDefaultTipDetails(cartResponse?.data);

                    scrollElementIntoView(CART_ELEMENT_IDS.DE_TIP_CONTAINER, false);
                    setDeliveryTab(DELIVERY_SECTION_ELEMENT.TIP);
                    clickBillDetailAddTip(clickEvent);
                    updateTipDetails(defaultTipDetails);
                    break;
                }
                case VALID_RENDERING_BUTTON_TYPES.POSITIVE_DELIVERY_CHARGES:
                case VALID_RENDERING_BUTTON_TYPES.POSITIVE_DELIVERY_CHARGES_EXCLUSIVE:
                    renderHalfcardOnMakeItFreeClick({
                        setShowM2ChalfcardOnClick,
                        setBillDetailsOneCardComms,
                        clickEvent,
                        planDetails,
                        itemData,
                    });
                    break;
                default: {
                    break;
                }
            }
        },
        [
            setShowM2ChalfcardOnClick,
            setBillDetailsOneCardComms,
            clickEvent,
            planDetails,
            cartResponse?.data,
            setDeliveryTab,
            updateTipDetails,
        ],
    );

    const doNotShowCollapsableBill = isBillNotCollapsible(collapsedBillDetails, collapseBillDetailXPVariant);

    const [isBillDetailSectionUncollapsed, setBillDetailSectionUncollapsed] = useSessionStorage(
        "is_bill_detail_section_uncollapsed",
        initialUncollapsedState,
    );

    const {
        onClickInfoIcon,
        impressionTooltipInfoText,
        onImpressionDeliveryFeeLineItem,
        onImpressionDeliveryFeeUnCollapsedLineItem,
        onImpressionBillTotalGroupingActual,
        onImpressionBillTotalGrouping,
        onImpressionBillViewChevron,
        onImpressionCollapsibleBillUpsell,
        onClickBillViewChevron,
        onImpressionSavingsStamp,
        onImpressionOfferCashRewardLineItem,
    } = useCartRendererAnalytics(cartResponse);
    const { onImpressionTaxesAndChargesLineItem } = useBillDetailsAnalytics(cartResponse);

    const savingsStampLottieInfo = useMemo(
        () => ({
            xpEnabled: cartResponse?.data?.XPData?.savings_stamp_enabled === BOOLEAN_AS_STRING.TRUE,
            title: cartResponse?.data?.savings_stamp_details?.lottie_title ?? "",
            subtitle: cartResponse?.data?.savings_stamp_details?.lottie_subtitle ?? "",
        }),
        [
            cartResponse?.data?.XPData?.savings_stamp_enabled,
            cartResponse?.data?.savings_stamp_details?.lottie_subtitle,
            cartResponse?.data?.savings_stamp_details?.lottie_title,
        ],
    );

    const { totalSavings, discountType, isOfferPack, freeDelCommsStyles } = billDetailItemsData;

    return {
        displayData: {
            totalSavings,
            discountType,
            onItemClick,
            onClickInfoIcon,
            isOfferPack,
        },
        tooltipDisplayData: {
            onClickInfoIcon,
            impressionTooltipInfoText,
            onItemClick,
            onImpressionDeliveryFeeLineItem,
            onImpressionDeliveryFeeUnCollapsedLineItem,
            onImpressionTaxesAndChargesLineItem,
            freeDelCommsStyles,
            onImpressionOfferCashRewardLineItem,
        },
        titleData: {
            onImpressionBillTotalGrouping,
            onImpressionBillTotalGroupingActual,
        },
        collapsibleWidgetData: {
            collapseBillDetailXPVariant,
            collapsedBillDetails,
            isBillDetailSectionUncollapsed,
            setBillDetailSectionUncollapsed,
            doNotShowCollapsableBill,
            onImpressionBillViewChevron,
            onClickBillViewChevron,
            onImpressionCollapsibleBillUpsell,
        },
        savingsStampData: {
            ...savingsStampLottieInfo,
            onImpressionSavingsStamp,
            isBillCollapsible: !doNotShowCollapsableBill,
            isBillUncollapsed: isBillDetailSectionUncollapsed,
        },
    };
};
